import React from 'react';
import ReactDOM from 'react-dom/client';
import './Notifications.css';
import { useStrings } from '../StringsContext';

// The big icon at the left of a toast: an emoji (data.icon) or, when data.iconSrc is set, an
// image such as a resource's own board art (/assets/resources/<file>.svg), so a toast about the
// Mailbox can show the Mailbox the player sees on the board.
function renderIcon(data, fallbackEmoji) {
  if (data?.iconSrc) return <img className="notification-icon-img" src={data.iconSrc} alt="" />;
  return data?.icon || fallbackEmoji;
}

// Global notification manager
let notificationRoot = null;
let notificationTimer = null;
let globalClickHandlers = {};

// Notification types that should persist until manually dismissed
const PERSISTENT_NOTIFICATION_TYPES = ['To Do', 'FTUE', 'Message'];

/**
 * Generic notification component
 */
const EXIT_MS = 350;      // slide back up behind the header (Notifications.css toastOut)
const AUTO_DISMISS_MS = 4600;

function Notification({ type, data, onDismiss, onClick }) {
    const strings = useStrings();
    const [exiting, setExiting] = React.useState(false);
    const exitTimer = React.useRef(null);

    // Play the slide-out, then unmount
    const dismiss = React.useCallback(() => {
        if (exitTimer.current) return;
        setExiting(true);
        exitTimer.current = setTimeout(onDismiss, EXIT_MS);
    }, [onDismiss]);

    React.useEffect(() => () => { if (exitTimer.current) clearTimeout(exitTimer.current); }, []);

    React.useEffect(() => {
        // Auto-dismiss (except for persistent notification types)
        if (!PERSISTENT_NOTIFICATION_TYPES.includes(type)) {
            const timer = setTimeout(dismiss, AUTO_DISMISS_MS);
            return () => clearTimeout(timer);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);
    
    // Helper function to replace {username} placeholder in text
    const replaceUsername = (text) => {
        if (!text || typeof text !== 'string') return text;
        return text.replace(/\{username\}/gi, data.username || 'Adventurer');
    };

    // Inline icons in toast text: "{icon:icon-farming.svg}" renders the SVG the nav column shows
    // for that panel (public/assets/icons), so a toast's icon matches the button it points at
    const renderMessage = (text) => {
        const t = replaceUsername(text);
        if (!t || typeof t !== 'string' || !t.includes('{icon:')) return t;
        return t.split(/(\{icon:[^}]+\})/g).map((part, i) => {
            const m = part.match(/^\{icon:([^}]+)\}$/);
            return m ? <img key={i} className="notification-inline-icon" src={`/assets/icons/${m[1]}`} alt="" /> : part;
        });
    };

    // Render different content based on notification type
    const renderContent = () => {
        switch (type) {
            case 'Trophy':
                return (
                    <>
                        <div className="notification-icon-wrapper">
                            <div className="notification-icon">{renderIcon(data, '🏆')}</div>
                            {data.type === 'Progress' && data.progress && (
                                <div className="notification-milestone">{data.progress}</div>
                            )}
                            {data.type === 'Count' && data.qty && (
                                <div className="notification-milestone">{data.qty}x</div>
                            )}
                        </div>
                        <div className="notification-text">
                            <div className="notification-title">{strings[6003]}</div>
                            <div className="notification-name">{data.name}</div>
                        </div>
                    </>
                );
            
            case 'Phase Change':
                return (
                    <>
                        <div className="notification-icon-wrapper">
                            <div className="notification-icon">{renderIcon(data, '🌙')}</div>
                        </div>
                        <div className="notification-text">
                            <div className="notification-title">Phase Change</div>
                            <div className="notification-name">{data.message || 'Day has turned to night'}</div>
                        </div>
                    </>
                );
            
            case 'Message':
                return (
                    <>
                        <div className="notification-icon-wrapper">
                            <div className="notification-icon">{renderIcon(data, '📨')}</div>
                        </div>
                        <div className="notification-text">
                            <div className="notification-title">{data.title || 'New Message'}</div>
                            <div className="notification-name">{data.message}</div>
                        </div>
                    </>
                );

            case 'To Do':
                return (
                    <>
                        <div className="notification-icon-wrapper">
                            <div className="notification-icon">{renderIcon(data, '✅')}</div>
                        </div>
                        <div className="notification-text">
                            <div className="notification-title">{data.title || strings[204]}</div>
                            <div className="notification-name">{data.message}</div>
                        </div>
                    </>
                );

            case 'Tip':
                return (
                    <>
                        <div className="notification-icon-wrapper">
                            <div className="notification-icon">{renderIcon(data, 'ℹ️')}</div>
                        </div>
                        <div className="notification-text">
                            <div className="notification-title">{data.title || strings[7001]}</div>
                            <div className="notification-name">{renderMessage(data.message)}</div>
                        </div>
                    </>
                );

            case 'Travel':
                return (
                    <>
                        <div className="notification-icon-wrapper">
                            <div className="notification-icon">{renderIcon(data, '👸')}</div>
                        </div>
                        <div className="notification-text">
                            <div className="notification-title">{data.title || strings[7001]}</div>
                            <div className="notification-name">{data.message}</div>
                        </div>
                    </>
                );

            case 'FTUE':
                return (
                    <>
                        <div className="notification-icon-wrapper">
                            <div className="notification-icon">{renderIcon(data, 'ℹ️')}</div>
                        </div>
                        <div className="notification-text">
                            <div className="notification-title">{data.title || strings[7049]}</div>
                            <div className="notification-name">{renderMessage(data.message)}</div>
                        </div>
                    </>
                );

            default:
                return (
                    <>
                        <div className="notification-icon-wrapper">
                            <div className="notification-icon">{renderIcon(data, 'ℹ️')}</div>
                        </div>
                        <div className="notification-text">
                            <div className="notification-title">Notification</div>
                            <div className="notification-name">{data.message || 'Something happened!'}</div>
                        </div>
                    </>
                );
        }
    };
    
    // Add persistent class for notifications that shouldn't auto-dismiss
    const isPersistent = PERSISTENT_NOTIFICATION_TYPES.includes(type);
    const className = `notification ${isPersistent ? 'notification-persistent' : ''} ${exiting ? 'notification-exiting' : ''}`;
    
    return (
        <div className={className} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
            <button className="notification-dismiss" onClick={(e) => {
                e.stopPropagation();
                dismiss();
            }}>×</button>
            <div className="notification-content">
                {renderContent()}
            </div>
        </div>
    );
}

/**
 * Shows a notification
 * @param {string} type - The type of notification ('Trophy', 'Phase Change', 'Message', etc.)
 * @param {Object} data - The data to display in the notification
 * @param {string} [data.icon] - Optional emoji to override the default notification icon
 * @param {string} [data.iconSrc] - Optional image URL shown instead of the emoji (e.g. a resource sprite)
 * @param {Function} onClick - Optional click handler for the notification
 */
/**
 * Register a global click handler for a notification type
 * @param {string} type - The notification type
 * @param {Function} handler - The click handler
 */
export function registerNotificationClickHandler(type, handler) {
    globalClickHandlers[type] = handler;
}

let currentDismiss = null;
// Remove the toast on screen, if any (FTUE steps with dismissNotification: true use it when the
// instruction has been followed).
export function dismissNotification() { if (currentDismiss) currentDismiss(); }

export function showNotification(type, data, onClick = null) {
    console.log(`[showNotification] Called with type: ${type}`, data);
    // Clear any existing notification
    if (notificationTimer) {
        clearTimeout(notificationTimer);
    }
    
    // Create or reuse the notification container
    let container = document.getElementById('notification-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'notification-container';
        // Append to game container instead of body
        const gameContainer = document.querySelector('.game-container') || document.querySelector('.App') || document.body;
        gameContainer.appendChild(container);
    }
    
    if (!notificationRoot) {
        notificationRoot = ReactDOM.createRoot(container);
    }
    
    const handleDismiss = () => {
        console.log(`[handleDismiss] Dismissing notification of type: ${type}`);
        if (notificationRoot) {
            notificationRoot.render(null);
        }
        if (currentDismiss === handleDismiss) currentDismiss = null;
    };
    currentDismiss = handleDismiss;
    
    // Use passed onClick or fall back to global handler for this type
    const clickHandler = onClick || (globalClickHandlers[type] ? () => globalClickHandlers[type](data) : null);
    
    // Use a unique key to force React to remount the component and trigger animation
    const notificationKey = `${type}-${Date.now()}`;
    
    notificationRoot.render(
        <Notification 
            key={notificationKey}
            type={type}
            data={data}
            onDismiss={handleDismiss}
            onClick={clickHandler}
        />
    );
}

if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') window.__showNotification = showNotification; // dev hook
